import { useState } from 'react'
import type { FormEvent } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { ArrowRight, Check, Loader2 } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { useLanguage } from '@/context/LanguageContext'
import { useWaitlist } from '@/context/WaitlistContext'
import { cn } from '@/lib/utils'
import type { ExchangeOption } from '@/lib/translations'

interface EmailFormProps {
  className?: string
  variant?: 'hero' | 'compact'
  onSuccess?: () => void
}

const exchangeOptions: ExchangeOption[] = ['newton', 'shakepay', 'other']

export function EmailForm({ className, variant = 'hero', onSuccess }: EmailFormProps) {
  const { t } = useLanguage()
  const { incrementCount } = useWaitlist()
  const navigate = useNavigate()
  const [email, setEmail] = useState('')
  const [selectedExchanges, setSelectedExchanges] = useState<ExchangeOption[]>([])
  const [otherExchange, setOtherExchange] = useState('')
  const [pricingAmount, setPricingAmount] = useState('')
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error' | 'existing' | 'link-sent'>('idle')
  const [error, setError] = useState('')
  const [exchangeError, setExchangeError] = useState('')
  const [pricingError, setPricingError] = useState('')
  const [existingEmail, setExistingEmail] = useState('')

  const validateEmail = (email: string) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
  }

  const toggleExchange = (ex: ExchangeOption) => {
    setSelectedExchanges((prev) =>
      prev.includes(ex) ? prev.filter((e) => e !== ex) : [...prev, ex]
    )
    setExchangeError('')
  }

  const handleRequestMagicLink = async () => {
    setStatus('loading')
    try {
      const response = await fetch('/api/send-magic-link', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: existingEmail }),
      })

      if (!response.ok) throw new Error('Failed to send magic link')

      setStatus('link-sent')
    } catch {
      setError(t.common.error)
      setStatus('existing')
    }
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setError('')
    setExchangeError('')
    setPricingError('')

    if (!validateEmail(email)) {
      setError(t.common.emailError)
      return
    }

    if (selectedExchanges.length === 0) {
      setExchangeError(t.emailForm.selectExchange)
      return
    }

    if (selectedExchanges.includes('other') && !otherExchange.trim()) {
      setExchangeError(t.emailForm.specifyExchange)
      return
    }

    if (!pricingAmount.trim()) {
      setPricingError('Please enter a pricing amount')
      return
    }

    setStatus('loading')

    try {
      // Submit to Cloudflare Pages Function
      const response = await fetch('/api/waitlist', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email,
          exchanges: selectedExchanges,
          otherExchange: selectedExchanges.includes('other') ? otherExchange.trim() : null,
          pricingPreference: pricingAmount.trim() || null,
          timestamp: new Date().toISOString(),
        }),
      })

      const data = await response.json()

      if (!response.ok) throw new Error('Submission failed')

      // Check if email already exists
      if (data.isExisting) {
        setExistingEmail(data.email)
        setStatus('existing')
        return
      }

      setStatus('success')
      incrementCount()
      onSuccess?.()

      // FIX #5: Store edit token in sessionStorage instead of URL
      // This prevents token exposure in browser history and shared links
      if (data.editToken) {
        sessionStorage.setItem('waitlist_edit_token', data.editToken)
      }

      // Redirect to thanks page with only non-sensitive data in URL
      const params = new URLSearchParams({
        email: data.email || email.toLowerCase().trim(),
        position: String(data.position || data.count || 1),
      })

      // Small delay to show success state before redirect
      setTimeout(() => {
        navigate(`/thanks?${params.toString()}`)
      }, 800)
    } catch {
      // Show error - don't let user proceed if submission failed
      setStatus('error')
      setError(t.common.error)
    }
  }

  const isHero = variant === 'hero'

  return (
    <form
      onSubmit={handleSubmit}
      className={cn(
        'relative',
        isHero ? 'w-full max-w-[560px] mx-auto' : 'w-full',
        className
      )}
    >
      <AnimatePresence mode="wait">
        {status === 'success' ? (
          <motion.div
            key="success"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            className="flex items-center justify-center gap-3 py-5 bg-profit-500/10 border border-profit-500/30 rounded-2xl px-6"
          >
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: 'spring', stiffness: 500, damping: 30 }}
              className="w-10 h-10 bg-profit-500 rounded-full flex items-center justify-center"
            >
              <Check className="w-6 h-6 text-white" />
            </motion.div>
            <span className="text-profit-400 font-semibold text-lg">{t.common.success} You're on the list!</span>
          </motion.div>
        ) : status === 'existing' ? (
          <motion.div
            key="existing"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            className="flex flex-col items-center justify-center gap-3 py-6 bg-gold-500/10 border border-gold-500/30 rounded-2xl px-6"
          >
            <span className="text-gold-400 font-semibold text-lg">{t.common.alreadyRegistered}</span>
            <span className="text-slate-400 text-sm text-center">{t.common.alreadyRegisteredHint}</span>
            <motion.button
              type="button"
              onClick={handleRequestMagicLink}
              whileHover={{ y: -2 }}
              whileTap={{ scale: 0.98 }}
              className="mt-2 px-6 py-3 text-base font-semibold rounded-xl bg-gold-400 text-midnight-950 hover:bg-gold-300 transition-colors"
            >
              {t.common.requestMagicLink}
            </motion.button>
            {error && <p className="text-sm text-loss-400">{error}</p>}
          </motion.div>
        ) : status === 'link-sent' ? (
          <motion.div
            key="link-sent"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            className="flex flex-col items-center justify-center gap-3 py-6 bg-profit-500/10 border border-profit-500/30 rounded-2xl px-6"
          >
            <div className="flex items-center gap-3">
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                className="w-10 h-10 bg-profit-500 rounded-full flex items-center justify-center"
              >
                <Check className="w-6 h-6 text-white" />
              </motion.div>
              <span className="text-profit-400 font-semibold text-lg">{t.common.magicLinkSent}</span>
            </div>
            <span className="text-slate-400 text-sm text-center">{t.common.checkSpam}</span>
          </motion.div>
        ) : (
          <motion.div
            key="form"
            initial={false}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="space-y-6"
          >
            {/* Email Input Row */}
            <div className="flex flex-col sm:flex-row gap-4">
              {/* Email Input */}
              <div className="flex-1 min-w-0">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value)
                    setError('')
                    if (status === 'error') setStatus('idle')
                  }}
                  placeholder={isHero ? t.hero.emailPlaceholder : t.finalCta.emailPlaceholder}
                  disabled={status === 'loading'}
                  className={cn(
                    'w-full px-6 py-5 text-lg font-medium rounded-xl',
                    'bg-white text-slate-900 placeholder-slate-400',
                    'border-none outline-none',
                    'shadow-lg shadow-black/25',
                    'focus:ring-2 focus:ring-gold-400/50',
                    'transition-all duration-200',
                    'disabled:opacity-60 disabled:cursor-not-allowed',
                    error && 'ring-2 ring-loss-400'
                  )}
                />
                {error && (
                  <p className="mt-2 text-sm text-loss-400 text-center sm:text-left">{error}</p>
                )}
              </div>

              {/* Submit Button */}
              <motion.button
                type="submit"
                disabled={status === 'loading'}
                whileHover={{ y: -3 }}
                whileTap={{ scale: 0.98 }}
                className={cn(
                  'px-8 py-5 text-lg font-bold rounded-xl whitespace-nowrap',
                  'bg-gold-400 text-midnight-950',
                  'shadow-lg shadow-gold-500/40',
                  'hover:shadow-xl hover:shadow-gold-500/50',
                  'transition-all duration-200',
                  'disabled:opacity-60 disabled:cursor-not-allowed',
                  'flex items-center justify-center gap-2'
                )}
              >
                {status === 'loading' ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    {t.common.loading}
                  </>
                ) : (
                  <>
                    {isHero ? t.hero.cta : t.finalCta.cta}
                    <ArrowRight className="w-5 h-5" />
                  </>
                )}
              </motion.button>
            </div>

            {/* Exchange Selection - Checkbox Style */}
            <div className="text-center">
              <p className="text-slate-400 text-base mb-1">
                {t.emailForm.exchangeLabel}
              </p>
              <p className="text-slate-500 text-sm mb-4">
                {t.emailForm.exchangeLabelHint}
              </p>
              <div className="flex flex-wrap justify-center gap-x-6 gap-y-3">
                {exchangeOptions.map((ex) => {
                  const isSelected = selectedExchanges.includes(ex)
                  return (
                    <label
                      key={ex}
                      className={cn(
                        'flex items-center gap-2 cursor-pointer text-base transition-colors duration-200',
                        isSelected ? 'text-gold-400' : 'text-slate-400 hover:text-slate-300'
                      )}
                    >
                      <div
                        className={cn(
                          'w-5 h-5 rounded border-2 flex items-center justify-center transition-all duration-200',
                          isSelected
                            ? 'bg-gold-500 border-gold-500'
                            : 'border-slate-500 hover:border-slate-400'
                        )}
                        onClick={() => toggleExchange(ex)}
                      >
                        {isSelected && <Check className="w-3 h-3 text-midnight-950" strokeWidth={3} />}
                      </div>
                      <span className="font-medium" onClick={() => toggleExchange(ex)}>
                        {t.emailForm.exchanges[ex]}
                      </span>
                    </label>
                  )
                })}
              </div>
              {/* Other Exchange Text Input */}
              <AnimatePresence>
                {selectedExchanges.includes('other') && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.2 }}
                    className="mt-4 overflow-hidden"
                  >
                    <input
                      type="text"
                      value={otherExchange}
                      onChange={(e) => {
                        setOtherExchange(e.target.value)
                        setExchangeError('')
                      }}
                      placeholder={t.emailForm.otherPlaceholder}
                      disabled={status === 'loading'}
                      className={cn(
                        'w-full max-w-xs mx-auto px-4 py-3 text-base font-medium rounded-lg',
                        'bg-white/10 text-white placeholder-slate-400',
                        'border border-slate-600 outline-none',
                        'focus:ring-2 focus:ring-gold-400/50 focus:border-gold-400',
                        'transition-all duration-200',
                        'disabled:opacity-60 disabled:cursor-not-allowed'
                      )}
                    />
                  </motion.div>
                )}
              </AnimatePresence>
              {exchangeError && (
                <p className="mt-3 text-sm text-loss-400">{exchangeError}</p>
              )}
            </div>

            {/* Pricing Preference Question */}
            <div className="text-center">
              <p className="text-slate-400 text-base mb-1">
                How much would you pay monthly? <span className="text-loss-400">*</span>
              </p>
              <p className="text-slate-500 text-sm mb-4">
                Help us understand what pricing works for you
              </p>
              <div className="flex items-center justify-center gap-2 max-w-xs mx-auto">
                <span className="text-slate-400 text-lg font-medium">$</span>
                <input
                  type="text"
                  inputMode="decimal"
                  value={pricingAmount}
                  onChange={(e) => {
                    // Allow only numbers and decimal point
                    const value = e.target.value.replace(/[^0-9.]/g, '')
                    setPricingAmount(value)
                    setPricingError('')
                  }}
                  placeholder="0"
                  disabled={status === 'loading'}
                  className={cn(
                    'w-24 px-4 py-3 text-center text-lg font-medium rounded-lg',
                    'bg-white/10 text-white placeholder-slate-500',
                    'border border-slate-600 outline-none',
                    'focus:ring-2 focus:ring-gold-400/50 focus:border-gold-400',
                    'transition-all duration-200',
                    'disabled:opacity-60 disabled:cursor-not-allowed',
                    pricingError && 'ring-2 ring-loss-400 border-loss-400'
                  )}
                />
                <span className="text-slate-400 text-base">/month</span>
              </div>
              {pricingError && (
                <p className="mt-3 text-sm text-loss-400">{pricingError}</p>
              )}
            </div>

            {/* FIX #11: Privacy notice for IP storage */}
            <p className="mt-4 text-xs text-slate-500 text-center">
              By signing up, you agree to our collection of your email and IP address
              for waitlist management. We never share your data with third parties.
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </form>
  )
}
